import { z } from 'zod';
import { IdpSchema, OrgRoleSchema } from './enums.js';

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  idp: IdpSchema,
  isSystemAdmin: z.boolean(),
  status: z.enum(['active', 'disabled']),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
});
export type User = z.infer<typeof UserSchema>;

export const UserSummarySchema = UserSchema.pick({
  id: true,
  email: true,
  name: true,
  avatarUrl: true,
});
export type UserSummary = z.infer<typeof UserSummarySchema>;

export const DevLoginInputSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(200),
});
export type DevLoginInput = z.infer<typeof DevLoginInputSchema>;

export const MeMembershipSchema = z.object({
  orgId: z.string(),
  orgName: z.string(),
  orgSlug: z.string(),
  plan: z.enum(['free', 'paid']),
  role: OrgRoleSchema,
  /** Organisation AI switch, so the web can hide AI affordances. */
  aiEnabled: z.boolean(),
});

export const MeAssessorOrgSchema = z.object({
  orgId: z.string(),
  orgName: z.string(),
  assessmentId: z.string().nullable(),
  /** Organisation AI switch (assessor pre-checks are AI features too). */
  aiEnabled: z.boolean(),
});

export const MePendingInvitationSchema = z.object({
  id: z.string(),
  orgName: z.string(),
  role: OrgRoleSchema,
  expiresAt: z.string(),
});

/** When a platform admin is impersonating: who the real actor is. */
export const ImpersonationSchema = z.object({
  by: z.object({ id: z.string(), name: z.string(), email: z.string() }),
});
export type Impersonation = z.infer<typeof ImpersonationSchema>;

export const MeResponseSchema = z.object({
  user: UserSchema,
  memberships: z.array(MeMembershipSchema),
  assessorOrgs: z.array(MeAssessorOrgSchema),
  pendingInvitations: z.array(MePendingInvitationSchema),
  /** Set only while a system admin is viewing as this user. */
  impersonating: ImpersonationSchema.nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/**
 * Public preview of an invitation, keyed only by its one-time token, so the
 * sign-in screen can show which providers the inviting organisation allows.
 * Deliberately omits the invitee email and org id — the token alone must not
 * disclose who was invited.
 */
export const InvitationPreviewSchema = z.object({
  orgName: z.string(),
  role: OrgRoleSchema,
  allowedIdps: z.array(z.string()).nullable(),
  expiresAt: z.string(),
});
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;
